import React, { useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { Heart, BookOpen, Home as HomeIcon, Wrench, Users, Star, ChevronRight, GraduationCap, HandHeart } from 'lucide-react'
import './Home.css'

const stats = [
  { value: '500+', label: 'Children Supported' },
  { value: '60+', label: 'Rural Villages Covered' },
  { value: '16', label: 'Years of Service' },
  { value: '₹+', label: 'Funds Raised' },
]

const programs = [
  { icon: BookOpen, title: 'Free Education', desc: 'Quality schooling for BPL tribal children, completely free of cost — books, uniforms and supplies included.', color: '#0ea5e9' },
  { icon: HomeIcon, title: 'Hostel Facility', desc: 'Safe, clean residential facilities for children from remote villages, ensuring continuity in their education.', color: '#14b8a6' },
  { icon: GraduationCap, title: 'Free Tuition', desc: 'After-school tutoring and remedial classes to ensure no child falls behind or drops out.', color: '#6366f1' },
  { icon: Wrench, title: 'Skill Development', desc: 'Vocational training and life-skills workshops preparing children and youth for dignified livelihoods.', color: '#f59e0b' },
  { icon: HandHeart, title: 'Child Welfare', desc: 'Nutritious meals, healthcare check-ups, and emotional support for holistic child development.', color: '#ec4899' },
  { icon: Users, title: 'Community Training', desc: 'Empowering parents and tribal communities with awareness and capacity-building sessions.', color: '#10b981' },
]

const testimonials = [
  { name: 'Sunita Majhi', role: 'Parent, Malyabanta Village', text: 'My daughter would never have gone to school without this samiti. Today she dreams of being a teacher herself.' },
  { name: 'Ranjan Soren', role: 'Beneficiary (Class 8)', text: 'The hostel facility means I don\'t have to walk 12 km each day. I can study well and attend every class.' },
  { name: 'Priya Hembram', role: 'Skill Training Graduate', text: 'The tailoring course gave me a skill I use every day. I now earn for my family while my siblings study here.' },
]

function AnimatedSection({ children, className }) {
  const ref = useRef(null)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { ref.current.classList.add('visible'); observer.disconnect() } },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])
  return <div ref={ref} className={`reveal-section ${className || ''}`}>{children}</div>
}

export default function Home() {
  return (
    <div className="home">
      {/* Hero */}
     {/*  
      <section className="hero">  */}

      <section className="hero">
   <div className="hero__bg">
    <div className="hero__orb hero__orb--1"/>
    <div className="hero__orb hero__orb--2"/>
    <div className="hero__orb hero__orb--3"/>
  </div>
  <div className="container hero__content">

    {/* Logo image + name side by side */}
    <div className="hero__brand">
      <img
        src={require('./Malyabanta_Swechhasevi_Samiti.png')}
        alt="Malyabanta Swechhasevi Samiti"
        className="hero__logo-img"
      />
      <div className="hero__brand-text">
        <h2 className="hero__brand-name">
          Malyabanta<br/>Swechhasevi Samiti
        </h2>
        <span className="hero__brand-short">MSS · Est. 12.10.2000 ·Malkangiri Odisha, India</span>
      </div>
    </div>

    <p className="hero__desc">
      Providing free education, hostel facilities, skill training, and tuition
      to below-poverty-line tribal children — because every child deserves a chance.
    </p>
    <div className="hero__actions">
      <NavLink to="/donate" className="btn btn-primary hero__cta-primary">
        <Heart size={18} fill="currentColor"/> Donate &amp; Change a Life
      </NavLink>
      <NavLink to="/our-work" className="btn btn-outline">
        Our Work <ChevronRight size={16}/>
      </NavLink>
    </div>
    <div className="hero__stats">
      {stats.map(s => (
        <div key={s.label} className="hero__stat">
          <span className="hero__stat-val">{s.value}</span>
          <span className="hero__stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  </div>
  <div className="hero__scroll-hint"><span/></div>
</section>
{/* end of update logo change */}

      {/* Programs */}
      <section className="page-section programs">
        <div className="container">
          <AnimatedSection className="text-center">
            <span className="section-tag">What We Do</span>
            <h2 className="section-title">Programs &amp; Services</h2>
            <p className="section-desc">All our services are completely free of charge for children from below-poverty-line tribal families.</p>
          </AnimatedSection>
          <div className="programs__grid">
            {programs.map((prog, i) => (
              <AnimatedSection key={prog.title} style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="program-card card">
                  <div className="program-card__icon" style={{ background: prog.color + '20', color: prog.color }}>
                    <prog.icon size={26}/>
                  </div>
                  <h3 className="program-card__title">{prog.title}</h3>
                  <p className="program-card__desc">{prog.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Impact banner */}
      <section className="impact-banner">
        <div className="container impact-banner__inner">
          <div className="impact-banner__text">
            <span className="section-tag" style={{background:'rgba(255,255,255,0.15)', color:'white'}}>Our Impact</span>
            <h2 className="impact-banner__title">Transforming Communities,<br/>One Child at a Time</h2>
            <p className="impact-banner__desc">With your support, we have helped over 500 children from 60 villages access education and develop skills — breaking the cycle of poverty.</p>
            <NavLink to="/our-work" className="btn" style={{background:'white', color:'var(--sky-700)'}}>
              See Our Work <ChevronRight size={16}/>
            </NavLink>
          </div>
          <div className="impact-banner__cards">
            {stats.map(s => (
              <div key={s.label} className="impact-card">
                <div className="impact-card__val">{s.value}</div>
                <div className="impact-card__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="page-section testimonials">
        <div className="container">
          <AnimatedSection className="text-center">
            <span className="section-tag">Voices of Change</span>
            <h2 className="section-title">What Our Community Says</h2>
          </AnimatedSection>
          <div className="testimonials__grid">
            {testimonials.map((t, i) => (
              <AnimatedSection key={t.name}>
                <div className="testimonial-card card" style={{ animationDelay: `${i*0.1}s` }}>
                  <div className="testimonial-card__stars">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="#fbbf24" color="#fbbf24"/>)}
                  </div>
                  <p className="testimonial-card__text">"{t.text}"</p>
                  <div className="testimonial-card__author">
                    <div className="testimonial-card__avatar">{t.name[0]}</div>
                    <div>
                      <div className="testimonial-card__name">{t.name}</div>
                      <div className="testimonial-card__role">{t.role}</div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container cta-section__inner">
          <h2 className="cta-section__title">Be Part of This Mission</h2>
          <p className="cta-section__desc">Your donation directly funds education, hostel stays, meals and training for tribal children who have no other option.</p>
          <div className="cta-section__actions">
            <NavLink to="/donate" className="btn btn-amber">
              <Heart size={18} fill="currentColor"/> Donate Now
            </NavLink>
            <NavLink to="/contact" className="btn btn-outline" style={{borderColor:'rgba(255,255,255,0.4)', color:'white'}}>
              Get In Touch
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  )
}
